// Rychly (i zpetny) zapis materialu ke konkretnimu dni — ze stranky Material.
//
// - Den → operativa daneho dne pres GET /material/dny?od=d&do=d (jedna operativa na den).
// - Vyber polozky + radky Co/Kolik; ulozeni pres PUT /polozky/{id}/report s materialem
//   = dosavadni material polozky (cerstve nacteny) + nove radky. Backend material
//   nahrazuje celym seznamem, proto se posila vse.
// - Zapis nerusi schvaleni operativy a jde i u uzavreneho dne (viz STAVY_S_REPORTEM).

import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { STAVY_S_REPORTEM } from '@/lib/permissions';
import { formatDateCs } from '@/lib/utils';
import type { MaterialDen, MaterialInput, Polozka, PolozkaReportInput } from '@/types/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { richToPlain } from '@/components/ui/rich-text';
import { useToast } from '@/components/ui/use-toast';
import { StavBadge } from '@/components/StavBadge';
import { MaterialAutocomplete } from '@/components/MaterialAutocomplete';

interface Props {
  open: boolean;
  /** Predvyplneny den (YYYY-MM-DD). */
  datum: string;
  onClose: () => void;
}

const prazdnyRadek = (): MaterialInput => ({ co: '', kolik: '' });

function zkrat(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

export function MaterialZapisDialog({ open, datum: vychoziDatum, onClose }: Props) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [datum, setDatum] = useState(vychoziDatum);
  const [polozkaId, setPolozkaId] = useState('');
  const [radky, setRadky] = useState<MaterialInput[]>([prazdnyRadek()]);

  useEffect(() => {
    if (!open) return;
    setDatum(vychoziDatum);
    setPolozkaId('');
    setRadky([prazdnyRadek()]);
  }, [open, vychoziDatum]);

  const denQuery = useQuery<MaterialDen[]>({
    queryKey: ['material-dny', datum, datum],
    queryFn: () =>
      api<MaterialDen[]>(
        `/material/dny?od=${encodeURIComponent(datum)}&do=${encodeURIComponent(datum)}`,
      ),
    enabled: open && datum !== '',
  });
  const den = denQuery.data?.[0] ?? null;
  const lzeZapisovat = den !== null && STAVY_S_REPORTEM.includes(den.stav);

  // Jedina polozka dne → rovnou vybrana; pri zmene dne vyber zahodit.
  useEffect(() => {
    if (!den) {
      setPolozkaId('');
      return;
    }
    setPolozkaId((cur) =>
      den.polozky.some((p) => p.id === cur)
        ? cur
        : den.polozky.length === 1
          ? den.polozky[0].id
          : '',
    );
  }, [den]);

  const vybrana = den?.polozky.find((p) => p.id === polozkaId) ?? null;

  const noveRadky = radky
    .map((r) => ({ co: r.co.trim(), kolik: r.kolik?.trim() ?? '' }))
    .filter((r) => r.co !== '')
    .map((r) => ({ co: r.co, kolik: r.kolik === '' ? null : r.kolik }));

  const ulozitMutation = useMutation({
    mutationFn: async (_pokracovat: boolean) => {
      // Cerstvy material polozky — at neprepiseme, co mezitim zapsal nekdo jiny.
      const aktualni = await api<Polozka>(`/polozky/${polozkaId}`);
      const input: PolozkaReportInput = {
        material: [
          ...aktualni.material.map((m) => ({ co: m.co, kolik: m.kolik })),
          ...noveRadky,
        ],
      };
      return api<Polozka>(`/polozky/${polozkaId}/report`, {
        method: 'PUT',
        body: JSON.stringify(input),
      });
    },
    onSuccess: (_data, pokracovat) => {
      queryClient.invalidateQueries({ queryKey: ['material-dny'] });
      queryClient.invalidateQueries({ queryKey: ['material-spotreba'] });
      queryClient.invalidateQueries({ queryKey: ['polozka', polozkaId] });
      if (den) queryClient.invalidateQueries({ queryKey: ['operativa', den.operativaId] });
      toast({ title: 'Materiál zapsán', description: formatDateCs(datum) });
      if (pokracovat) {
        setRadky([prazdnyRadek()]);
      } else {
        onClose();
      }
    },
    onError: (err: Error) =>
      toast({ title: 'Uložení selhalo', description: err.message, variant: 'destructive' }),
  });

  const muzeUlozit =
    lzeZapisovat && polozkaId !== '' && noveRadky.length > 0 && !ulozitMutation.isPending;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (muzeUlozit) ulozitMutation.mutate(false);
  };

  const upravRadek = (i: number, zmena: Partial<MaterialInput>) =>
    setRadky((rs) => rs.map((r, j) => (j === i ? { ...r, ...zmena } : r)));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Zapsat materiál</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="zapis-datum">Den</Label>
              <Input
                id="zapis-datum"
                type="date"
                value={datum}
                onChange={(e) => setDatum(e.target.value)}
                className="w-44"
              />
            </div>
            {den && (
              <div className="pb-2">
                <StavBadge stav={den.stav} type="operativa" />
              </div>
            )}
          </div>

          {denQuery.isLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : denQuery.isError ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              Načtení selhalo: {(denQuery.error as Error).message}
            </div>
          ) : !den ? (
            <p className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
              Pro tento den není založená operativa.
            </p>
          ) : !lzeZapisovat ? (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              Operativa tohoto dne ještě není schválená — materiál půjde zapsat po schválení.
            </div>
          ) : den.polozky.length === 0 ? (
            <p className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
              Operativa tohoto dne nemá žádné položky.
            </p>
          ) : (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="zapis-polozka">Položka</Label>
                <Select value={polozkaId} onValueChange={setPolozkaId}>
                  <SelectTrigger id="zapis-polozka">
                    <SelectValue placeholder="Vyberte položku operativy" />
                  </SelectTrigger>
                  <SelectContent>
                    {den.polozky.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        #{p.poradi} {p.mechanizaceKod} — {zkrat(richToPlain(p.popisCinnosti), 70)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {vybrana && vybrana.material.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Už zapsáno:{' '}
                    {vybrana.material
                      .map((m) => (m.kolik ? `${m.co} (${m.kolik})` : m.co))
                      .join(', ')}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Nový materiál</Label>
                {radky.map((r, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <MaterialAutocomplete
                      value={r.co}
                      onChange={(v) => upravRadek(i, { co: v })}
                      placeholder="Co (např. štěrk)"
                      className="flex-1"
                    />
                    <Input
                      value={r.kolik ?? ''}
                      onChange={(e) => upravRadek(i, { kolik: e.target.value })}
                      onKeyDown={(e) => {
                        // Enter v poslednim "Kolik" = dalsi radek (rychle psani bez mysi).
                        if (e.key === 'Enter' && i === radky.length - 1) {
                          e.preventDefault();
                          setRadky((rs) => [...rs, prazdnyRadek()]);
                        }
                      }}
                      placeholder="Kolik (5 m, 2 ks…)"
                      className="w-40"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={radky.length === 1}
                      onClick={() => setRadky((rs) => rs.filter((_, j) => j !== i))}
                      title="Odebrat řádek"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setRadky((rs) => [...rs, prazdnyRadek()])}
                >
                  <Plus className="mr-1.5 h-4 w-4" />
                  Další řádek
                </Button>
              </div>
            </>
          )}

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Zavřít
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={!muzeUlozit}
              onClick={() => ulozitMutation.mutate(true)}
              title="Uložit a zapsat další materiál (třeba k jiné položce nebo dni)"
            >
              Uložit a další
            </Button>
            <Button type="submit" disabled={!muzeUlozit}>
              {ulozitMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Uložit'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
